import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatFormFieldControl,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { FlexLayoutModule, MediaObserver } from '@ngbracket/ngx-layout';
import { Store, select } from '@ngrx/store';

import { AppState, selectMainCompany } from '@qtek/libs/store';
import { FullTime, Time } from '@qtek/shared/models';
import { getTimeIntervals } from '@qtek/shared/utils';

import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { MatOptionModule } from '@angular/material/core';
import { ErrorComponent } from '../error/error.component';

@Component({
  selector: 'qt-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
    { provide: MatFormFieldControl, useExisting: TimePickerComponent },
  ],
  // tslint:disable use-host-property-decorator
  host: {
    '[class.floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgFor,
    NgIf,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    ErrorComponent,
  ],
})
export class TimePickerComponent
  implements
    ControlValueAccessor,
    MatFormFieldControl<Time>,
    OnInit,
    OnDestroy,
    AfterViewInit
{
  static nextId = 0;

  hostControl: NgControl;

  hours: string[];

  timeArray: FullTime[];

  filteredTimes$: Observable<FullTime[]>;

  form: UntypedFormGroup;

  focused = false;

  isValueChange = false;

  ngControl: NgControl = null;

  stateChanges = new Subject<void>();

  errorState = false;

  controlType = 'qt-time-picker';

  id = `qt-time-picker-${TimePickerComponent.nextId++}`;

  describedBy = '';

  company: any;

  destroy$ = new Subject<void>();

  get shouldLabelFloat(): boolean {
    return this.isValueChange;
  }

  get empty() {
    const time = this.form.get('fullTime').value;
    return !time.hour && !time.minutes;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }

  @Input()
  set qtDisabled(value: boolean) {
    this.disabled = value;
    if (this.form) {
      if (value) {
        this.form.get('fullTime').disable();
      } else {
        this.form.get('fullTime').enable();
      }
    }
  }

  @Input()
  get value() {
    return this._time;
  }

  set value(val: Time) {
    this._time = val;
    this.propagateChange(val);
  }

  @Input() hint: string;

  @Input()
  set isPlaceholders(value: any) {
    this._placeholder = coerceBooleanProperty(value) ? this._placeholder : '';
    this.stateChanges.next();
  }

  // tslint:disable variable-name
  private _time: Time;
  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  // tslint:enable variable-name

  constructor(
    public media: MediaObserver,
    private fb: UntypedFormBuilder,
    private fm: FocusMonitor,
    private elRef: ElementRef,
    private store: Store<AppState>,
    private inj: Injector
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(selectMainCompany), take(1)).subscribe(company => {
      this.company = company;

      this.hours = Array.from({ length: 12 }, (_, i) => this.addLeadingZero(i));
      this.timeArray = this.getHours();

      this.form = this.fb.group({
        fullTime: this.fb.control({
          value: this.timeArray[0]?.time,
          disabled: this.disabled,
        }),
      });

      if (this.required) {
        this.form.get('fullTime').setValidators(Validators.required);
      }

      this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.isValueChange = data.fullTime;
        if (data && data.fullTime && data.fullTime.time) {
          this.value = {
            hour: data.fullTime.time.hour,
            minutes: data.fullTime.time.minutes,
          };
        } else if (data && data.fullTime && !data.fullTime.time) {
          this.form.get('fullTime').setErrors({ time: true });
        }
      });

      if (this.ngControl) {
        this.ngControl.valueAccessor = this;
      }
    });
  }

  // ngAfterViewInit() {
  //   this.hostControl = this.inj.get(NgControl);
  //   const control = this.hostControl.control;

  //   const origFunc = control.markAsDirty;
  //   const self = this;
  //   control.markAsDirty = function () {
  //     origFunc.apply(this, arguments as any);
  //     if (control.dirty) {
  //       self.form.get('fullTime').markAsDirty();
  //     }
  //   };
  // }
  ngAfterViewInit() {
    this.hostControl = this.inj.get(NgControl);
    const control = this.hostControl.control;

    const originalMarkAsDirty = control.markAsDirty.bind(control);

    control.markAsDirty = () => {
      originalMarkAsDirty();

      if (control.dirty) {
        const fullTimeControl = this.form.get('fullTime');
        if (fullTimeControl) {
          fullTimeControl.markAsDirty();
        }
      }
    };
  }

  displayFn(time: any): string {
    return time ? time.name + time.type : time;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateChange: any = () => {};

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  registerOnTouched() {}

  writeValue(value: any) {
    if (typeof value !== 'undefined' && value) {
      let searchedTime: FullTime = this.searchTime(value.hour, value.minutes);

      if (!searchedTime && value.hour && value.minutes) {
        const type = value.hour > 12 ? 'pm' : 'am';
        searchedTime = this.getSingleHour(
          (value.hour % 12).toString(),
          value.minutes,
          type
        );
        this.timeArray.push(searchedTime);
      }

      this.value = searchedTime.time;
      this.form.get('fullTime').setValue(searchedTime);
    } else {
      this.form.get('fullTime').setValue('');
    }
  }

  searchTime(hour: string, minutes: string): FullTime {
    return this.timeArray.filter(
      fullTime =>
        fullTime.time.hour === hour && fullTime.time.minutes === minutes
    )[0];
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
    this.destroy$.next();
    this.destroy$.complete();
  }

  setDisabledState(isDisabled: boolean) {
    this.qtDisabled = isDisabled;
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onContainerClick(event: MouseEvent) {}

  selectTime(inputValue: string, inputRef: HTMLElement): void {
    if (inputValue) {
      this.setTime(inputValue);
      // inputRef.blur();
    }
  }

  setTime(name?: string): void {
    let timeObject = null;
    if (name) {
      timeObject = this.timeArray.find(
        fullTime =>
          name.toLowerCase() ===
          `${fullTime.name.toLowerCase()}${fullTime.type.toLowerCase()}`
      );
    }
    if (timeObject) {
      this.form.get('fullTime').setValue(timeObject);
    }
  }

  private getHours(): FullTime[] {
    let timeIntervalArray: FullTime[] = this.getTimeByType('am');
    timeIntervalArray = timeIntervalArray.concat(this.getTimeByType('pm'));
    return timeIntervalArray;
  }

  private getTimeByType(type: string): FullTime[] {
    const timeIntervalArray: FullTime[] = [];
    const timeIntervals = getTimeIntervals(this.company, 0, 59);
    for (const hour of this.hours) {
      for (const interval of timeIntervals) {
        timeIntervalArray.push(this.getSingleHour(hour, interval, type));
      }
    }
    return timeIntervalArray;
  }

  private getSingleHour(hour: string, minutes: string, type: string): FullTime {
    let hourValue = +hour;
    if (type === 'pm') {
      hourValue += 12;
    }
    return {
      name: `${hour === '00' ? '12' : hour}:${minutes}`,
      type,
      time: {
        hour: type === 'pm' ? hourValue.toString() : hour,
        minutes,
      },
    };
  }

  private addLeadingZero(n: number) {
    if (n < 0) {
      throw new Error('[qtek] Can not add 0 to nagative number.');
    }

    const prefix = n < 10 ? '0' : '';

    return prefix + n;
  }
}
